<script lang="ts">
  import { createContext } from 'reka-ui'

  export interface ModalDialogProps {
    /** If the dialog should be closed when the user clicks outside the component. Is false by default */
    dismissable?: boolean
    /** If the top-right close button should be hidden. Is false by default (button is shown). */
    hideCloseButton?: boolean
    maxWidth?: number
    fullScreenMobile?: boolean
    /** Stretch the dialog to fill the whole viewport. */
    fullScreen?: boolean
  }

  export interface ModalDialogContext {
    /** If the dialog is provided. Used by sub-components to check if the dialog is provided. */
    provided: boolean
  }

  const [_useModalDialog, provideModalDialog] =
    createContext<ModalDialogContext>('gs-gat-dialog')

  export function useModalDialogContext() {
    try {
      return _useModalDialog()
    } catch {
      return {
        provided: false
      }
    }
  }
</script>

<script setup lang="ts">
  import { globalUtils } from '@gat-ship/utils'
  import { IconClose } from '@gat-ship/icons'
  import { testIds } from '@gat-ship/types'
  import { DialogClose } from 'reka-ui'
  import { useSlots, onMounted, reactive, computed } from 'vue'
  import { useDialogZIndex } from '../../composables/useDialogZIndex'
  import { useModalStackEntry } from '../../lib/modal-stack'
  import { Dialog, DialogTrigger, DialogScrollContent } from '../ui/dialog'

  defineOptions({ inheritAttrs: false })

  const props = defineProps<ModalDialogProps>()

  provideModalDialog({
    provided: true
  })

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:save')
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  const slots = useSlots()

  function onOutsideClick(event: Event) {
    event.preventDefault()
    if (props.dismissable === true) {
      isOpen.value = false
    }
  }

  const { zIndex } = useDialogZIndex(isOpen)

  // When rendered as a central-host stack entry, render non-modal so the stack
  // (not reka's last-mounted-wins layering) owns interaction order: raising a
  // buried modal must make it interactive. Only the top entry keeps a dim,
  // pointer-blocking overlay; buried ones are muted behind it. See
  // `useModalStackEntry`.
  const { isHostStackEntry, isTop } = useModalStackEntry()

  const showCloseButton = computed(() => !props.hideCloseButton)
</script>
<template>
  <Dialog v-model:open="isOpen" :modal="!isHostStackEntry">
    <DialogTrigger v-if="slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>
    <DialogScrollContent
      :overlay-z-index="zIndex"
      :non-modal="isHostStackEntry"
      :overlay-muted="isHostStackEntry && !(isTop && isOpen)"
      :class="[
        $attrs.class,
        'flex w-fit min-w-[350px] shrink flex-col gap-0 rounded-lg p-0 shadow-lg',
        fullScreenMobile && 'max-md:w-full max-md:min-w-0 max-md:rounded-none'
      ]"
      :max-width="maxWidth"
      :full-screen-mobile="fullScreenMobile"
      :full-screen="fullScreen"
      :show-close-icon="false"
      @interact-outside="onOutsideClick"
    >
      <slot />

      <DialogClose
        v-if="showCloseButton"
        class="ring-offset-background focus:ring-ring focus:outline-hidden bg-background shadow-xs absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border transition-opacity hover:cursor-pointer hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
        :data-testid="testIds.common.dialog.closeX"
        @click="isOpen = false"
      >
        <icon-close class="size-3.5" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogScrollContent>
  </Dialog>
</template>
